import { FieldState, FormState } from 'formstate';
import { action, computed, makeObservable, observable, ObservableMap, runInAction } from 'mobx';
import {
    formStateToJS,
    InputFieldState,
    commitFormState,
    setFormStateValues,
} from '@servicetitan/form-state';
import {
    TenantConfig,
    ITenantDetail,
    TenantManagementApi,
} from '@servicetitan/marketing-tenant-management-api';
import { inject, injectable } from '@servicetitan/react-ioc';
import {
    DEFAULT_CONFIGURATION_VALIDATORS,
    REQUIRED_CONFIGURATION_VALIDATORS,
    SENDER_DOMAIN_VALIDATORS,
} from '../utils/validators';
import { normalizeWarmupDomains } from '../utils/warmup-domains';
import { LoadStatus } from '../../common/enums/load-status';
import { withTrimmedValues } from '../utils/with-trimmed-values';
import { sortFeatureGates } from '../utils/sorters';
import { isPartnerWithTelecom } from '../telecom/utils/partners.utils';

@injectable()
export class ManageTenantStore {
    formState = new FormState({
        emailScope: new InputFieldState('').validators(...DEFAULT_CONFIGURATION_VALIDATORS),
        emailSenderDomain: new InputFieldState('').validators(...SENDER_DOMAIN_VALIDATORS),
        directMailProvider: new InputFieldState('').validators(...DEFAULT_CONFIGURATION_VALIDATORS),
        authPath: new InputFieldState(''),
        emailTemplateOptOut: new FieldState(false),
        warmupDomains: new FieldState<string[]>([]),
    });

    @observable tenant?: ITenantDetail;
    @observable saveLoadStatus = LoadStatus.None;
    @observable pageLoadStatus = LoadStatus.None;
    @observable showTelecom = false;

    partnerGenericConfigs: ObservableMap<string> = observable.map([], { deep: false });
    tenantGenericConfigs: ObservableMap<string> = observable.map([], { deep: false });

    @computed get isFormChanged() {
        return (
            this.formState.$.authPath.dirty ||
            this.formState.$.directMailProvider.dirty ||
            this.formState.$.emailScope.dirty ||
            this.formState.$.emailSenderDomain.dirty ||
            this.formState.$.emailTemplateOptOut.dirty ||
            this.formState.$.warmupDomains.dirty
        );
    }

    constructor(
        @inject(TenantManagementApi) private readonly tenantManagementApi: TenantManagementApi,
    ) {
        makeObservable(this);
    }

    init = async (partnerExternalId: string, tenantExternalId: string) => {
        this.formState.disableAutoValidation();
        this.setupShowTelecom(partnerExternalId);

        runInAction(() => {
            this.pageLoadStatus = LoadStatus.Loading;
        });

        try {
            const { data: tenant } = await this.tenantManagementApi.tenant(
                partnerExternalId,
                tenantExternalId,
            );

            const {
                configs: {
                    emailSenderDomain,
                    emailScope,
                    directMailProvider,
                    authPath,
                    emailTemplateOptOut,
                    warmupDomains,
                    generic: tenantGenericConfigs,
                },
                partnerDetail: {
                    configs: { isPartnerLevelAuth, generic: partnerGenericConfigs },
                },
            } = tenant;

            setFormStateValues(this.formState, {
                authPath: authPath || '',
                emailScope,
                emailSenderDomain,
                directMailProvider,
                emailTemplateOptOut: emailTemplateOptOut ?? false,
                warmupDomains: normalizeWarmupDomains(warmupDomains ?? []),
            });

            commitFormState(this.formState);

            if (isPartnerLevelAuth === false) {
                this.formState.$.authPath.validators(...REQUIRED_CONFIGURATION_VALIDATORS);
            }

            runInAction(() => {
                this.tenant = tenant;
                this.partnerGenericConfigs.replace(sortFeatureGates(partnerGenericConfigs));
                this.tenantGenericConfigs.replace(sortFeatureGates(tenantGenericConfigs));
                this.pageLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.pageLoadStatus = LoadStatus.Error;
            });
        }
    };

    setupShowTelecom = (partnerExternalId: string) => {
        this.showTelecom = isPartnerWithTelecom(partnerExternalId);
    };

    saveConfigs = async () => {
        if (!this.tenant || this.saveLoadStatus === LoadStatus.Loading) {
            return;
        }

        try {
            await this.formState.enableAutoValidationAndValidate();

            if (this.formState.hasError) {
                return;
            }

            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Loading;
            });

            const {
                directMailProvider,
                emailSenderDomain,
                emailScope,
                authPath,
                emailTemplateOptOut,
                warmupDomains,
            } = formStateToJS(this.formState);

            const tenantConfig = new TenantConfig({
                ...this.tenant.configs,
                emailScope,
                emailSenderDomain,
                emailTemplateOptOut,
                directMailProvider,
                warmupDomains,
                generic: {}, // generic configs are not saved by this BE method
            });

            if (this.tenant.partnerDetail.configs.isPartnerLevelAuth === false) {
                tenantConfig.authPath = authPath;
            }

            const body = withTrimmedValues(tenantConfig);

            await this.tenantManagementApi.configPUT3(this.tenant.internalId, body);

            commitFormState(this.formState);

            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Error;
            });
        }
    };

    @action resetConfiguration = () => {
        this.formState.reset();
        this.saveLoadStatus = LoadStatus.None;
    };
}
